<template>
  <div :class="getSectionClassNames">
    <h2
      :class="[css.sectionHeader, $style.collapsableSectionHeader]"
      @click="handleClick"
    >
      <CollapseCaret
        v-if="showCaret"
        :collapsed="collapsed"
      />
      <div
        :class="$style.titleContainer"
      >
        <slot name="title" />
        <div :class="$style.titleContent">
          {{ title }}
          <component
            :is="headerIcon"
            v-if="headerIcon"
            :class="$style.icon"
          />
          <IconTooltip v-if="tooltipContent">
            {{ tooltipContent }}
            <IconTooltipReadMore
              v-if="tooltipKbLink"
              :kbLink="tooltipKbLink"
            />
          </IconTooltip>
          <NewBadge v-if="showNewBadge" />
        </div>
        <slot
          name="icon"
        />
        <div
          v-if="showSecondaryTitleContent"
          :class="$style.secondaryTitleContent"
        >
          <slot name="secondaryTitleContent" />
        </div>
      </div>
    </h2>
    <transition
      :enterActiveClass="$style.slideEnterActive"
      :enterClass="$style.slideEnter"
      :leaveActiveClass="$style.slideLeaveActive"
      :leaveToClass="$style.slideLeaveTo"
      @after-enter="$emit('opened')"
      @after-leave="$emit('closed')"
    >
      <div
        v-show="!collapsed"
        :class="customSectionContentClass"
      >
        <slot />
      </div>
    </transition>
  </div>
</template>

<script>
import CollapseCaret from 'client/components/ContentSection/CollapseCaret.vue';
import IconTooltip from 'client/components/Tooltip/IconTooltip.vue';
import IconTooltipReadMore from 'client/components/Tooltip/IconTooltipReadMore.vue';
import NewBadge from 'client/components/Badges/NewBadge.vue';
import css from 'client/css/sections.css';

export default {
  name: 'CollapsableSection',

  inject: ['$validator'],

  components: {
    CollapseCaret,
    IconTooltip,
    IconTooltipReadMore,
    NewBadge,
  },

  props: {
    collapse: {
      type: Boolean,
      default: true,
    },
    customSectionContentClass: {
      type: String,
      default: css.sectionContent,
    },
    delayTransition: {
      type: Boolean,
      default: false,
    },
    headerIcon: {
      type: Object,
      default: null,
    },
    showCaret: {
      type: Boolean,
      default: true,
    },
    showNewBadge: {
      type: Boolean,
      default: false,
    },
    showSecondaryTitleContent: {
      type: Boolean,
      default: true,
    },
    showSectionCards: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      required: true,
    },
    tooltipContent: {
      type: String,
      default: '',
    },
    tooltipKbLink: {
      type: String,
      default: '',
    },
    unsetOverflow: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      css,
      collapsed: this.collapse,
    };
  },

  computed: {
    getSectionClassNames() {
      const classNames = [css.sectionRoot, this.$style.collapsableSection];
      if (!this.showSectionCards) {
        classNames.push(this.$style.section);
      } else if (this.unsetOverflow) {
        classNames.push(this.$style.unsetOverflow);
      }
      return classNames;
    },
  },

  watch: {
    collapse(newValue) {
      this.collapsed = newValue;
    },
  },

  methods: {
    handleClick() {
      this.collapsed = !this.collapsed;
      this.$emit('toggleCollapse', this.collapsed);
    },
  },
};

/**
 * How to use the Collapsable Section
 *
 * <collapsable-section title="this section can collapse">
 * Content here you want to be collapsable
 * </collapsable-section>
 *
 * You don't have to pass a title.
 * <collapsable-section>Just a line and a caret above me</collapsable-section>
 *
 * You can start with the section open:
 * <collapsable-section title="Open when mounted" :collapse="false">
 * The world can see me
 * </collapsable-section>
 *
 */
</script>

<style scoped module>
@value lightShadow3 from '~css/shadows.css';
@value (slowEaseFade) from '~css/transitions.css';

.collapsableSection {
  border: var(--jc-border);
  border-radius: var(--jc-border-radius-x-small);
  box-shadow: lightShadow3;
  margin-bottom: var(--jc-spacer-small);
  overflow: hidden;
  padding-left: 20px;
}

.unsetOverflow {
  overflow: unset;
}

.collapsableSectionHeader {
  align-items: center;
  cursor: pointer;
  display: flex;
  font-size: var(--jcHeadingSmall);
  gap: var(--jc-spacer);
  margin: 0;
  padding: 0;
}

.icon {
  height: 16px;
  margin-left: 7px;
  position: relative;
  top: 2px;
  width: 16px;
}

.section.section {
  background-color: transparent;
  border: none;
  box-shadow: none;
}

.slideEnterActive,
.slideLeaveActive {
  transition: slowEaseFade;
}

.slideEnter,
.slideLeaveTo {
  opacity: 0;
  transform: translateY(-1em);
}

.titleContainer {
  align-items: center;
  display: flex;
  gap: var(--jc-spacer-small);
  width: 100%;
}

.titleContent {
  line-height: 1.325;
  overflow: hidden;
  text-overflow: ellipsis;
}

.secondaryTitleContent {
  margin-left: 10px;
}

.transitionDelay {
  transition-delay: 0.4s;
}
</style>
